import { Buffer } from 'buffer'

// 添加缓存机制
const parseCache = new Map();
const isHexCache = new Map();

// 缓存配置
const MAX_CACHE_SIZE = 100;
const CLEANUP_THRESHOLD = 80; // 当缓存达到80%时开始清理

// 检测是否为Java十进制字节数组格式
function isJavaDecimalArray(input) {
  // 优化：先进行快速检查，避免不必要的正则表达式
  const trimmed = input.trim();
  if (trimmed.length < 3) return false; // 至少需要 "1,2"
  
  // 检查是否包含逗号
  if (!trimmed.includes(',')) return false;
  
  // 检查是否只包含数字、逗号、空格和负号
  if (!/^[\d\s,\-]+$/.test(trimmed)) return false;
  
  // 最后进行详细的正则表达式验证
  const pattern = /^-?\d+\s*,\s*-?\d+(\s*,\s*-?\d+)*$/;
  return pattern.test(trimmed);
}

// 检测是否为Java字符串转义格式
function isJavaEscapeString(input) {
  // 优化：先进行快速检查
  if (!input.includes('\\')) return false;
  
  // 检查是否包含转义序列（包括常见的转义字符）
  const pattern = /\\[tnrbf"\\]|\\[0-7]{1,3}|\\[xX][0-9a-fA-F]{2}|\\[0-9]{1,3}/;
  return pattern.test(input);
}

// 解析Java十进制字节数组
function parseJavaDecimalArray(input) {
  // 优化：使用更高效的字符串处理
  const trimmed = input.trim();
  const parts = trimmed.split(',');
  const bytes = new Uint8Array(parts.length);
  
  for (let i = 0; i < parts.length; i++) {
    // 使用更快的数字解析，避免trim()调用
    const part = parts[i];
    let start = 0, end = part.length;
    
    // 跳过前导空格
    while (start < end && part[start] === ' ') start++;
    // 跳过尾随空格
    while (end > start && part[end - 1] === ' ') end--;
    
    const num = parseInt(part.substring(start, end));
    bytes[i] = num < 0 ? (256 + num) : num;
  }
  
  return Buffer.from(bytes);
}

// 解析Java字符串转义格式
function parseJavaEscapeString(input) {
  // 使用ISO-8859-1编码来模拟Java的getBytes(StandardCharsets.ISO_8859_1)
  const bytes = [];
  let i = 0;
  
  while (i < input.length) {
    if (input[i] === '\\') {
      // 处理转义序列
      i++; // 跳过反斜杠
      
      if (i >= input.length) {
        // 反斜杠在字符串末尾，忽略
        break;
      }
      
      const nextChar = input[i];
      
      // 处理常见的转义字符
      if (nextChar === 't') {
        bytes.push(9); // 制表符
        i++;
      } else if (nextChar === 'n') {
        bytes.push(10); // 换行符
        i++;
      } else if (nextChar === 'r') {
        bytes.push(13); // 回车符
        i++;
      } else if (nextChar === 'b') {
        bytes.push(8); // 退格符
        i++;
      } else if (nextChar === 'f') {
        bytes.push(12); // 换页符
        i++;
      } else if (nextChar === '"') {
        bytes.push(34); // 双引号
        i++;
      } else if (nextChar === "'") {
        bytes.push(39); // 单引号
        i++;
      } else if (nextChar === '\\') {
        bytes.push(92); // 反斜杠
        i++;
      } else if (/[0-7]/.test(nextChar)) {
        // 八进制转义序列
        let octStr = '';
        let j = i;
        while (j < input.length && j < i + 3 && /[0-7]/.test(input[j])) {
          octStr += input[j];
          j++;
        }
        const byte = parseInt(octStr, 8);
        bytes.push(byte);
        i = j;
      } else if (nextChar === 'x' && i + 2 < input.length) {
        // 十六进制转义序列
        const hexStr = input.substring(i + 1, i + 3);
        if (/[0-9a-fA-F]{2}/.test(hexStr)) {
          const byte = parseInt(hexStr, 16);
          bytes.push(byte);
          i += 3;
        } else {
          // 无效的十六进制，当作普通字符
          bytes.push(input.charCodeAt(i - 1) & 0xFF);
        }
      } else if (/[0-9]/.test(nextChar)) {
        // 十进制转义序列
        let decStr = '';
        let j = i;
        while (j < input.length && j < i + 3 && /[0-9]/.test(input[j])) {
          decStr += input[j];
          j++;
        }
        const byte = parseInt(decStr, 10);
        if (byte >= 0 && byte <= 255) {
          bytes.push(byte);
        }
        i = j;
      } else {
        // 无效的转义序列，当作普通字符
        bytes.push(input.charCodeAt(i) & 0xFF);
        i++;
      }
    } else {
      // 普通字符
      const charCode = input.charCodeAt(i);
      bytes.push(charCode & 0xFF); // 确保是单字节
      i++;
    }
  }
  
  return Buffer.from(bytes);
}

// 智能缓存清理函数
function cleanupCache(cache, maxSize) {
  const entries = Array.from(cache.entries());
  // 按访问次数和时间戳排序，优先保留访问次数多且最近访问的
  entries.sort((a, b) => {
    const scoreA = a[1].accessCount * 0.7 + (a[1].timestamp / 1000000) * 0.3;
    const scoreB = b[1].accessCount * 0.7 + (b[1].timestamp / 1000000) * 0.3;
    return scoreB - scoreA;
  });
  
  // 保留前maxSize个条目
  const toDelete = entries.slice(maxSize);
  toDelete.forEach(([key]) => cache.delete(key));
}

export function parseInput(input) {
  // 检查缓存
  if (parseCache.has(input)) {
    const cached = parseCache.get(input);
    cached.accessCount++;
    cached.timestamp = Date.now();
    return cached.result;
  }

  const trimmedInput = input.trim();
  let result;

  // 1. 检查是否为Java十进制字节数组格式
  if (isJavaDecimalArray(trimmedInput)) {
    result = parseJavaDecimalArray(trimmedInput);
  }
  // 2. 检查是否为Java字符串转义格式
  else if (isJavaEscapeString(trimmedInput)) {
    result = parseJavaEscapeString(trimmedInput);
  }
  // 3. 原有的hex和base64格式
  else {
    // 优化：减少字符串操作
    const normalizedInput = trimmedInput.replace(/\s/g, "");
    let normalizedHexInput = normalizedInput;
    
    // 只在包含0x时才进行替换
    if (normalizedInput.includes('0x')) {
      normalizedHexInput = normalizedInput.replace(/0x/g, "");
    }
    
    const lowerHexInput = normalizedHexInput.toLowerCase();
    
    if (isHex(lowerHexInput)) {
      result = Buffer.from(lowerHexInput, "hex");
    } else {
      result = Buffer.from(normalizedInput, "base64");
    }
  }

  // 缓存结果，添加时间戳用于LRU清理
  parseCache.set(input, {
    result,
    timestamp: Date.now(),
    accessCount: 1
  });
  
  // 智能缓存清理
  if (parseCache.size > CLEANUP_THRESHOLD) {
    cleanupCache(parseCache, MAX_CACHE_SIZE);
  }

  return result;
}

export function isHex(string) {
  // 检查缓存
  if (isHexCache.has(string)) {
    const cached = isHexCache.get(string);
    cached.accessCount++;
    cached.timestamp = Date.now();
    return cached.result;
  }

  // 优化：使用更高效的字符检查
  let result = true;
  const len = string.length;
  
  // 使用传统for循环，避免for...of的开销
  for (let i = 0; i < len; i++) {
    const char = string.charCodeAt(i);
    // 使用字符码比较，避免字符串比较
    if (!((char >= 97 && char <= 102) || (char >= 48 && char <= 57))) { // a-f: 97-102, 0-9: 48-57
      result = false;
      break;
    }
  }
  
  // 缓存结果，添加时间戳用于LRU清理
  isHexCache.set(string, {
    result,
    timestamp: Date.now(),
    accessCount: 1
  });
  
  // 智能缓存清理
  if (isHexCache.size > CLEANUP_THRESHOLD) {
    cleanupCache(isHexCache, MAX_CACHE_SIZE);
  }

  return result;
}

// 检测输入格式类型
export function detectInputFormat(input) {
  const trimmedInput = input.trim();
  
  if (isJavaDecimalArray(trimmedInput)) {
    return 'java-decimal-array';
  } else if (isJavaEscapeString(trimmedInput)) {
    return 'java-escape-string';
  } else {
    const normalizedInput = trimmedInput.replace(/\s/g, "");
    const normalizedHexInput = normalizedInput.replace(/0x/g, "").toLowerCase();
    
    if (isHex(normalizedHexInput)) {
      return 'hex';
    } else {
      return 'base64';
    }
  }
}

export function bufferLeToBeHex(buffer) {
  let output = "";
  for (const v of buffer) {
    const hex = v.toString(16);
    if (hex.length === 1) {
      output = "0" + hex + output;
    } else {
      output = hex + output;
    }
  }
  return output;
}

export const bufferToPrettyHex = b => [...b].map(c => c.toString(16).padStart(2, '0')).join(' ');

